import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSourcesStore, type SelectedFolders } from '@/stores/sources.store';
import { useQueryFiltersStore } from '@/stores/query-filters.store';
import { useQueryPanelStore } from '@/stores/query-panel.store';
import {
    buildUrlStateFromStores,
    deserializeFromUrlParams,
    serializeToUrlParams,
    getSortFromSearchParams,
    serializeQueryStateParams,
    SOURCE_MODE,
    URL_PARAMS,
} from '@/utils/query-url-params';
import { SortOption } from '@/utils/query-filters';
import { EngineService } from '@/services/engine';
import { useRetentionStartDate } from './useRetentionStartDate';

interface UseQueryUrlSyncOptions {
    debounceMs?: number;
}

async function resolveFolderMembers(
    folderIds: string[],
): Promise<SelectedFolders> {
    const knownFolders = useSourcesStore.getState().selectedFolders;
    const folders: SelectedFolders = new Map();
    await Promise.all(
        folderIds.map(async (folderId) => {
            const knownMembers = knownFolders.get(folderId);
            if (knownMembers) {
                folders.set(folderId, knownMembers);
                return;
            }
            try {
                const children =
                    await EngineService.getSourceFolderChildren(folderId);
                useSourcesStore.getState().addSources([...children.sources]);
                folders.set(
                    folderId,
                    children.sources.map((source) => source.id),
                );
            } catch {
                folders.set(folderId, []);
            }
        }),
    );
    return folders;
}

export function useQueryUrlSync(options: UseQueryUrlSyncOptions = {}) {
    const { debounceMs = 300 } = options;
    const [searchParams, setSearchParams] = useSearchParams();
    const retentionStart = useRetentionStartDate();
    const queryStateParams = useMemo(
        () => serializeQueryStateParams(searchParams),
        [searchParams],
    );

    const [isInitialized, setIsInitialized] = useState(false);
    const isSyncingFromUrlRef = useRef(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hasInitialSyncRef = useRef(false);

    const sourcesAll = useSourcesStore((state) => state.all);
    const sourcesSelected = useSourcesStore((state) => state.selected);
    const sourcesSelectedFolders = useSourcesStore(
        (state) => state.selectedFolders,
    );
    const sourcesExcluded = useSourcesStore((state) => state.excluded);
    const filtersFilters = useQueryFiltersStore((state) => state.filters);
    const selectedObjectClassIds = useQueryFiltersStore(
        (state) => state.selectedObjectClassIds,
    );

    useEffect(() => {
        const urlState = deserializeFromUrlParams(
            new URLSearchParams(queryStateParams),
            retentionStart,
        );
        const sourcesStore = useSourcesStore.getState();
        const filtersStore = useQueryFiltersStore.getState();

        isSyncingFromUrlRef.current = true;
        let cancelled = false;
        let pendingFolderResolution = false;
        let pendingSourcesBackfill = Promise.resolve();

        const backfillMissingSources = async (ids: string[]) => {
            try {
                const missingSourceIds = ids.filter(
                    (id) => !useSourcesStore.getState().getSourceById(id),
                );
                if (missingSourceIds.length === 0) return;
                const sources =
                    await EngineService.getSourcesByIds(missingSourceIds);
                useSourcesStore.getState().addSources(sources);
            } catch (error) {
                console.error('Failed to load sources from the URL', error);
            }
        };

        if (urlState.sources) {
            if (urlState.sources.mode === SOURCE_MODE.ALL) {
                sourcesStore.selectAll();
                urlState.sources.excluded.forEach(sourcesStore.deselectSource);
            } else if (urlState.sources.folders.length === 0) {
                sourcesStore.setSelectedSources(new Set(urlState.sources.ids));
                pendingSourcesBackfill = backfillMissingSources(
                    urlState.sources.ids,
                );
            } else {
                pendingFolderResolution = true;
                const looseSourceIds = urlState.sources.ids;
                resolveFolderMembers(urlState.sources.folders).then(
                    (folders) => {
                        if (cancelled) return;
                        const selectedIds = new Set(looseSourceIds);
                        folders.forEach((memberIds) =>
                            memberIds.forEach((id) => selectedIds.add(id)),
                        );
                        isSyncingFromUrlRef.current = true;
                        useSourcesStore
                            .getState()
                            .setSelectedSources(selectedIds, folders);
                        isSyncingFromUrlRef.current = false;
                        backfillMissingSources(looseSourceIds).then(() => {
                            setIsInitialized(true);
                        });
                    },
                );
            }
        } else {
            sourcesStore.clearSelection();
        }

        if (urlState.dateTime) {
            filtersStore.setDateTimeFilter(
                urlState.dateTime.mode,
                urlState.dateTime.customRange,
            );
        } else {
            filtersStore.clearDateTimeFilter();
        }

        if (urlState.confidence) {
            filtersStore.setConfidenceFilter(
                urlState.confidence.enabled,
                urlState.confidence.min,
                urlState.confidence.max,
            );
        } else {
            filtersStore.setConfidenceFilter(false);
        }

        if (urlState.objectTypes) {
            filtersStore.setObjectClassSelection(new Set(urlState.objectTypes));
        } else {
            filtersStore.clearObjectClassSelection();
        }

        filtersStore.setSceneOnly(urlState.sceneOnly === true);

        const panelStore = useQueryPanelStore.getState();

        if (urlState.attachment) {
            panelStore.setAttachmentFile({
                url: urlState.attachment.url,
                filename: urlState.attachment.filename,
            });
        } else {
            panelStore.clearAttachmentFile();
        }

        isSyncingFromUrlRef.current = false;
        if (!pendingFolderResolution) {
            pendingSourcesBackfill.then(() => {
                setIsInitialized(true);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [queryStateParams, retentionStart]);

    const syncUrlFromStores = useCallback(() => {
        if (!isInitialized || isSyncingFromUrlRef.current) {
            return;
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            const sourcesState = useSourcesStore.getState();
            const filtersState = useQueryFiltersStore.getState();

            const currentUrlState = deserializeFromUrlParams(
                searchParams,
                retentionStart,
            );
            const attachment = currentUrlState.attachment;
            const sortFromUrl = searchParams.has(URL_PARAMS.SORT)
                ? getSortFromSearchParams(searchParams, SortOption.CONFIDENCE)
                : undefined;

            const urlState = buildUrlStateFromStores(
                searchParams.get('q') || '',
                {
                    all: sourcesState.all,
                    selected: sourcesState.selected,
                    selectedFolders: sourcesState.selectedFolders,
                    excluded: sourcesState.excluded,
                },
                filtersState.filters,
                sortFromUrl,
                attachment,
                filtersState.getSelectedObjectClassIds(),
            );

            const newParams = serializeToUrlParams(urlState, searchParams);
            const currentParamsString = searchParams.toString();
            const newParamsString = newParams.toString();

            if (currentParamsString !== newParamsString) {
                setSearchParams(newParams, { replace: true });
            }
        }, debounceMs);
    }, [isInitialized, debounceMs, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized) return;

        if (!hasInitialSyncRef.current) {
            hasInitialSyncRef.current = true;
            return;
        }

        syncUrlFromStores();

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [
        syncUrlFromStores,
        sourcesAll,
        sourcesSelected,
        sourcesSelectedFolders,
        sourcesExcluded,
        filtersFilters,
        selectedObjectClassIds,
    ]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return {
        isInitialized,
    };
}
